import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { TbTrash } from "react-icons/tb";
import PhoneInput, { COUNTRY_CODES } from "src/components/PhoneInput/PhoneInput";
import { SpecialtyPicker } from "src/pages/Settings/SpecialtyPicker";
import { RegisterFormData } from "../registerTypes";

interface Props {
    data: RegisterFormData;
    setData: React.Dispatch<React.SetStateAction<RegisterFormData>>;
}

const MAX_IMG_BYTES = 5 * 1024 * 1024;

export default function StepAddress({ data, setData }: Props) {
    const { t } = useTranslation();
    const logoRef = useRef<HTMLInputElement>(null);
    const [logoError, setLogoError] = useState<string | null>(null);

    const set = <K extends keyof RegisterFormData>(k: K, v: RegisterFormData[K]) =>
        setData(d => ({ ...d, [k]: v }));

    const handleLogo = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        if (file.size > MAX_IMG_BYTES) {
            setLogoError(t("form.imageTooLarge"));
            return;
        }
        setLogoError(null);
        const reader = new FileReader();
        reader.onload = () => set("logo", reader.result as string);
        reader.readAsDataURL(file);
    };

    const addPhone = () =>
        set("phones", [...data.phones, { id: crypto.randomUUID(), value: "" }]);

    const removePhone = (id: string) =>
        set("phones", data.phones.filter(p => p.id !== id));

    const updatePhone = (id: string, value: string) =>
        set("phones", data.phones.map(p => p.id === id ? { ...p, value } : p));

    return (
        <div className="register-step">

            {/* Specialization */}
            <div className="form-group register-step__specialty">
                <label>{t("register.specialization")} *</label>
                <SpecialtyPicker value={data.specialization} onChange={v => set("specialization", v)} />
            </div>

            {/* Street & number (combined) + PLZ */}
            <div className="register-step__row">
                <div className="form-group register-step__pct75">
                    <label>{t("register.streetAndNumber")} *</label>
                    <input
                        type="text"
                        value={data.street}
                        onChange={e => set("street", e.target.value)}
                        placeholder="z.B. Musterstraße 12"
                    />
                </div>
                <div className="form-group register-step__pct25">
                    <label>{t("register.plz")} *</label>
                    <input
                        type="text"
                        inputMode="numeric"
                        maxLength={5}
                        value={data.plz}
                        onChange={e => set("plz", e.target.value.replace(/\D/g, "").slice(0, 5))}
                        placeholder="z.B. 80331"
                    />
                </div>
            </div>

            {/* City 50% + Country 50% */}
            <div className="register-step__row">
                <div className="form-group register-step__half">
                    <label>{t("register.city")} *</label>
                    <input
                        type="text"
                        value={data.city}
                        onChange={e => set("city", e.target.value)}
                        placeholder="z.B. München"
                    />
                </div>
                <div className="form-group register-step__half">
                    <label>{t("register.country")}</label>
                    <select
                        className="settings-select"
                        value={data.country}
                        onChange={e => set("country", e.target.value)}
                    >
                        <option value="">—</option>
                        {COUNTRY_CODES.map(c => (
                            <option key={c.name} value={c.name}>{c.flag} {c.name}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Phones + Logo */}
            <div className="register-step__row register-step__row--top">
                <div className="form-group register-step__half">
                    <label>{t("register.phones")} *</label>
                    {data.phones.map(p => (
                        <div key={p.id} className="register-step__phone-row">
                            <PhoneInput value={p.value} onChange={v => updatePhone(p.id, v)} />
                            <button type="button"
                                className="register-step__icon-btn register-step__icon-btn--danger"
                                onClick={() => removePhone(p.id)}>
                                <TbTrash />
                            </button>
                        </div>
                    ))}
                    <button type="button" className="register-step__add-btn" onClick={addPhone}>
                        {t("register.addPhone")}
                    </button>
                </div>

                <div className="register-step__half">
                    <label>{t("register.logo")}</label>
                    <div className="register-step__logo-area register-step__logo-area--wide"
                        onClick={() => logoRef.current?.click()}>
                        {data.logo
                            ? <img src={data.logo} alt="logo" className="register-step__logo-preview" />
                            : <span className="register-step__logo-placeholder">{t("register.logoUpload")}</span>
                        }
                    </div>
                    <input ref={logoRef} type="file" accept="image/*"
                        className="register-step__file-hidden" onChange={handleLogo} />
                    {logoError && <p className="avatar-upload__error">{logoError}</p>}
                </div>
            </div>

        </div>
    );
}
